/**
 * Agent Chat view — F1-F4 inquiry + F9 execution.
 *
 * Upgraded from V5 basic chat to incorporate the full lineage:
 *   V1 scaffold: Gate phase track, skill discovery, MCP tool calls
 *   V3 terminalWidget: Command history (↑/↓), slash commands, manifest display
 *   V4 portalSpa: MembraneLanguage, Allen & Co aesthetic
 *
 * The agent chat is a terminal-engageable surface — the user can type
 * commands or natural language. Pi routes through the capability registry.
 * The gate phase track shows progress through intake → advise → prepare → build.
 * The manifest displays the agent identity and surface context.
 *
 * Allen & Co aesthetic: white paper, black ink, editorial spacing.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";

interface Message {
  role: "user" | "agent" | "error";
  content: string;
  timestamp: number;
  skill?: string;
}

interface GatePhase {
  key: string;
  name: string;
  status: "pending" | "in_progress" | "completed" | "skipped";
}

interface GateSummary {
  gate_id: string;
  tenant_id: string;
  phases: GatePhase[];
  build_unlocked: boolean;
  current: GatePhase | null;
}

interface Skill {
  skill_id: string;
  purpose: string;
  execution_mode: string;
  frontier_route: string;
  configurable: boolean;
  subskills?: string[];
  capability_refs?: string[];
}

// Phase → skill mapping (from V1 scaffold)
const PHASE_SKILLS: Record<string, string> = {
  intake: "skill_intake_v1",
  advise: "skill_advise_v1",
  prepare: "skill_prepare_v1",
  build: "skill_build_v1",
  f0_inquiry: "skill_f0_inquiry_v1",
  f1_enumerate: "skill_f1_enumerate_v1",
  f2_grammar: "skill_f2_grammar_v1",
  f3_bind: "skill_f3_bind_v1",
  f4_eligibility: "skill_f4_eligibility_v1",
  f5_seal: "skill_f5_seal_v1",
};

const PHASE_LABELS: Record<string, string> = {
  intake: "Intake",
  advise: "Advise",
  prepare: "Prepare",
  build: "Build",
  f0_inquiry: "Inquiry",
  f1_enumerate: "Enumerate",
  f2_grammar: "Grammar",
  f3_bind: "Bind",
  f4_eligibility: "Eligibility",
  f5_seal: "Seal",
};

const PHASE_ICONS: Record<string, string> = {
  pending: "○",
  in_progress: "●",
  completed: "✓",
  skipped: "−",
};

export function AgentChatView({ isOperator }: { isOperator: boolean }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState<GateSummary | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [showSkills, setShowSkills] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [manifest, setManifest] = useState<any>(null);
  const [f3Packet, setF3Packet] = useState<any>(null);
  const [governor, setGovernor] = useState<any>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const membraneBypass = isOperator;

  // Load chat history + gate state + manifest
  useEffect(() => {
    const clients = getClients();
    if (!clients) return;

    // Load gate state via MCP (V1 scaffold pattern)
    clients.control.mcpToolCall("gate.summary", {})
      .then((res: any) => {
        const data = res?.result || res;
        if (data?.gate_id) setGate(data);
      })
      .catch(() => {
        // No active gate — that's fine, chat still works
      });

    clients.control.mcpToolCall("skills.list", {})
      .then((res: any) => {
        setSkills(res?.result || res || []);
      })
      .catch(() => {});

    clients.control.getChatHistory()
      .then((res: any) => {
        const history = (res?.messages || []).map((m: any) => ({
          role: m.role === "user" ? "user" : "agent",
          content: applyMembrane(m.content, membraneBypass),
          timestamp: m.timestamp || Date.now(),
          skill: m.metadata?.skill_id,
        }));
        setMessages(history);
      })
      .catch(() => {})
      .finally(() => {
        if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
      });

    // Load manifest (V3 terminalWidget pattern)
    fetch("/api/agent/manifest", { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (d?.ok) setManifest(d); })
      .catch(() => {});

    // Load F3 settlement packet
    clients.control.getHermesF3Packet()
      .then((res: any) => { if (res?.ok) setF3Packet(res.packet); })
      .catch(() => {});

    // Load governor state
    clients.control.getAgentGovernor()
      .then((res: any) => { if (res?.ok) setGovernor(res); })
      .catch(() => {});
  }, [isOperator]);

  const send = useCallback(async () => {
    if (!input.trim() || busy) return;
    const content = input.trim();

    // Add to history (V3 terminalWidget pattern)
    setHistory(prev => [...prev, content].slice(-50));
    setHistoryIdx(-1);

    setInput("");
    setBusy(true);
    setMessages((prev) => [...prev, { role: "user", content, timestamp: Date.now() }]);

    try {
      const clients = getClients();
      if (!clients) throw new Error("Not authenticated");

      // Slash commands (V3 terminalWidget lineage)
      if (content.startsWith("/")) {
        const parts = content.split(/\s+/);
        const verb = parts[0].toLowerCase();
        const args = parts.slice(1).join(" ");

        if (verb === "/skills" || verb === "/help") {
          const skillList = skills.length > 0 ? skills : [];
          if (skillList.length === 0) {
            setMessages(prev => [...prev, {
              role: "agent", content: "No skills loaded yet. The gate will populate skills as you progress.", timestamp: Date.now()
            }]);
          } else {
            const list = skillList.map(s =>
              `  ${s.skill_id.replace(/^skill_/, "").replace(/_v\d+$/, "")} — ${applyMembrane(s.purpose, membraneBypass)}`
            ).join("\n");
            setMessages(prev => [...prev, {
              role: "agent", content: `Available skills:\n${list}`, timestamp: Date.now()
            }]);
          }
          return;
        }

        if (verb === "/advance") {
          if (!gate?.current) {
            setMessages(prev => [...prev, { role: "error", content: "No active gate to advance.", timestamp: Date.now() }]);
            return;
          }
          await clients.control.mcpToolCall("gate.complete", {
            gate_id: gate.gate_id,
            phase_key: gate.current!.key,
        evidence_refs: [],
            summary: `Phase ${gate.current!.name} completed via agent chat`,
          });
          const res = await clients.control.mcpToolCall("gate.summary", { gate_id: gate.gate_id });
          const data = res?.result || res;
          if (data?.gate_id) setGate(data);
          setMessages(prev => [...prev, {
            role: "agent", content: `Phase ${gate.current!.name} completed. ${data.current ? `Next: ${data.current.name}` : "Gate complete."}`, timestamp: Date.now()
          }]);
          return;
        }

        if (verb === "/gate") {
          if (!gate) {
            setMessages(prev => [...prev, { role: "agent", content: "No active gate. Start one from Work Packet.", timestamp: Date.now() }]);
            return;
          }
          const phases = gate.phases.map(p => `  ${PHASE_ICONS[p.status] || "○"} ${p.name} — ${p.status}`).join("\n");
          setMessages(prev => [...prev, {
            role: "agent", content: `Gate: ${gate.gate_id}\n${phases}\nBuild: ${gate.build_unlocked ? "unlocked" : "locked"}`, timestamp: Date.now()
          }]);
          return;
        }

        if (verb === "/manifest") {
          if (!manifest) {
            setMessages(prev => [...prev, { role: "agent", content: "No manifest loaded.", timestamp: Date.now() }]);
            return;
          }
          setMessages(prev => [...prev, {
            role: "agent", content: `Agent: ${manifest.agent_identity || manifest.label || "unknown"}\nSurface: ${manifest.subdomain || "portal"}\nService: ${manifest.service || "getrida"}`, timestamp: Date.now()
          }]);
          return;
        }

        if (verb === "/governor") {
          if (!governor) {
            setMessages(prev => [...prev, { role: "agent", content: "Governor state not loaded.", timestamp: Date.now() }]);
            return;
          }
          setMessages(prev => [...prev, {
            role: "agent", content: `Governor: ${governor.active_missions} active, ${governor.blocked_missions} blocked\nLast tick: ${governor.last_supervision_tick || "—"}`, timestamp: Date.now()
          }]);
          return;
        }

        if (verb === "/mission") {
          if (!governor?.missions?.length) {
            setMessages(prev => [...prev, { role: "agent", content: "No active missions.", timestamp: Date.now() }]);
            return;
          }
          const list = governor.missions.map((m: any) =>
            `  ${m.status} — ${m.mission_id.slice(0, 12)}…`
          ).join("\n");
          setMessages(prev => [...prev, {
            role: "agent", content: `Missions:\n${list}`, timestamp: Date.now()
          }]);
          return;
        }

        if (verb === "/f3") {
          if (!f3Packet) {
            setMessages(prev => [...prev, { role: "agent", content: "No F3 packet loaded.", timestamp: Date.now() }]);
            return;
          }
          setMessages(prev => [...prev, {
            role: "agent", content: `F3 Settlement:\n  Packet: ${f3Packet.packet_id}\n  Mission: ${f3Packet.mission}\n  Authority: ${f3Packet.authority}\n  Source: ${f3Packet.provenance?.source || "—"}`, timestamp: Date.now()
          }]);
          return;
        }

        // Unknown slash command
        setMessages(prev => [...prev, {
          role: "error", content: `Unknown command: ${verb}. Available: /skills, /advance, /gate, /manifest, /governor, /mission, /f3, /help`, timestamp: Date.now()
        }]);
        return;
      }

      // Normal message → Pi
      const res: any = await clients.control.postChat(content);
      const reply = applyMembrane(res.reply || res.response?.content || "Done.", membraneBypass);
      const skill = res.response?.skill_id || res.skill_id;
      setMessages((prev) => [...prev, { role: "agent", content: reply, timestamp: Date.now(), skill }]);

      // Refresh gate state after chat (phase may have advanced)
      clients.control.mcpToolCall("gate.summary", {})
        .then((r: any) => {
          const d = r?.result || r;
          if (d?.gate_id) setGate(d);
        })
        .catch(() => {});
    } catch (err: any) {
      setMessages((prev) => [...prev, { role: "error", content: err.message, timestamp: Date.now() }]);
    } finally {
      setBusy(false);
      if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [input, busy, gate, skills, manifest, membraneBypass]);

  const advancePhase = useCallback(async () => {
    if (!gate?.current) return;
    setAdvancing(true);
    try {
      const clients = getClients();
      if (!clients) return;
      await clients.control.mcpToolCall("gate.complete", {
        gate_id: gate.gate_id,
        phase_key: gate.current!.key,
        evidence_refs: [],
        summary: `Phase ${gate.current!.name} completed via portal`,
      });
      // Refresh gate
      const res = await clients.control.mcpToolCall("gate.summary", { gate_id: gate.gate_id });
      const data = res?.result || res;
      if (data?.gate_id) setGate(data);
    } catch (err: any) {
      setMessages((prev) => [...prev, { role: "error", content: `Phase advance failed: ${err.message}`, timestamp: Date.now() }]);
    } finally {
      setAdvancing(false);
    }
  }, [gate]);

  // History navigation (V3 terminalWidget pattern)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (history.length === 0) return;
      const newIdx = historyIdx < 0 ? history.length - 1 : Math.max(0, historyIdx - 1);
      setHistoryIdx(newIdx);
      setInput(history[newIdx]);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIdx < 0) return;
      const newIdx = historyIdx + 1;
      if (newIdx >= history.length) { setHistoryIdx(-1); setInput(""); }
      else { setHistoryIdx(newIdx); setInput(history[newIdx]); }
      return;
    }
  }, [send, history, historyIdx]);

  const currentPhaseKey = gate?.current?.key || "";
  const currentSkillId = PHASE_SKILLS[currentPhaseKey] || "skill_f0_inquiry_v1";
  const currentPhaseLabel = gate?.current?.name || PHASE_LABELS[currentPhaseKey] || "Inquiry";

  return (
    <div className="view chat-view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("Agent", membraneBypass)}</span>
        <h1>{applyMembrane("Your Agent", membraneBypass)}</h1>
      </div>

      {/* Manifest display — V3 terminalWidget lineage */}
      {manifest && (
        <div className="agent-manifest">
          <div className="manifest-row">
            <span className="manifest-key">agent</span>
            <span className="manifest-val">{applyMembrane(manifest.agent_identity || manifest.label || "agent", membraneBypass)}</span>
          </div>
          {manifest.subdomain && (
            <div className="manifest-row">
              <span className="manifest-key">surface</span>
              <span className="manifest-val">{manifest.subdomain.replace("https://", "")}</span>
            </div>
          )}
        </div>
      )}

      {/* F3 Settlement banner */}
      {f3Packet && (
        <div className="f3-settlement-banner" style={{
          background: "var(--panel-bg, #12161c)",
          border: "1px solid var(--border, #28313b)",
          borderRadius: 12, padding: "10px 14px", marginBottom: 12,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <span className="badge" data-status={f3Packet.authority === "GO" ? "active" : "pending"}>
            F3 Settled
          </span>
          <span className="mono muted">{f3Packet.packet_id}</span>
          <span className="muted">authority: {f3Packet.authority}</span>
        </div>
      )}

      {/* Governor panel */}
      {governor && (governor.active_missions > 0 || governor.blocked_missions > 0) && (
        <div className="governor-panel" style={{
          background: "var(--panel-bg, #12161c)",
          border: "1px solid var(--border, #28313b)",
          borderRadius: 12, padding: "10px 14px", marginBottom: 12,
        }}>
          <div className="card-title" style={{ fontSize: "0.85rem" }}>Governor</div>
          <div className="card-meta" style={{ marginTop: 4 }}>
            <span className="badge" data-status="active">{governor.active_missions} active</span>
            {governor.blocked_missions > 0 && (
              <span className="badge" data-status="inactive">{governor.blocked_missions} blocked</span>
            )}
            {governor.last_supervision_tick && (
              <span className="muted">last tick: {governor.last_supervision_tick}</span>
            )}
          </div>
        </div>
      )}

      {/* Gate Phase Track — from V1 scaffold lineage */}
      {gate && gate.phases && gate.phases.length > 0 && (
        <div className="gate-phase-track">
          {gate.phases.map((phase, i) => (
            <div key={i} className={`gate-phase-step ${phase.status}`}>
              <div className="gate-phase-dot">
                {PHASE_ICONS[phase.status] || phase.key[0].toUpperCase()}
              </div>
              <div className="gate-phase-label">{applyMembrane(phase.name, membraneBypass)}</div>
              <div className="gate-phase-sublabel">{phase.status.replace("_", " ")}</div>
            </div>
          ))}
        </div>
      )}

      {/* Build status + phase advancement — from V1 scaffold */}
      {gate && (
        <div className="gate-status-row">
          <span className={`badge`} data-status={gate.build_unlocked ? "active" : "pending"}>
            {applyMembrane(gate.build_unlocked ? "Build: Ready" : "Build: Locked", membraneBypass)}
          </span>
          {gate.current && gate.current.key !== "build" && (
            <button
              className="btn-secondary gate-advance-btn"
              onClick={advancePhase}
              disabled={advancing}
            >
              {advancing ? "Completing…" : `Complete ${applyMembrane(gate.current.name, membraneBypass)} →`}
            </button>
          )}
          <button
            className="btn-secondary gate-skills-btn"
            onClick={() => setShowSkills(!showSkills)}
          >
            {showSkills ? "Hide skills" : `Skills (${skills.length})`}
          </button>
        </div>
      )}

      {/* Skill discovery panel — from V1 scaffold */}
      {showSkills && skills.length > 0 && (
        <div className="skill-panel">
          {skills.map((skill, i) => {
            const isActive = skill.skill_id === currentSkillId;
            return (
              <div key={i} className={`skill-card ${isActive ? "active" : ""}`}>
                <div className="skill-title">
                  {skill.skill_id.replace(/^skill_/, "").replace(/_v\d+$/, "")}
                  {isActive && <span className="badge" data-status="active">current</span>}
                </div>
                <div className="skill-purpose muted">{applyMembrane(skill.purpose, membraneBypass)}</div>
                <div className="skill-meta">
                  <span className="badge">{skill.execution_mode}</span>
                  <span className="badge">{applyMembrane(skill.frontier_route, membraneBypass)}</span>
                  {skill.subskills && skill.subskills.length > 0 && (
                    <span className="badge" data-status="pending">{skill.subskills.length} subskills</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Chat log — terminal-engageable */}
      <div className="chat-container">
        <div className="chat-log" ref={logRef}>
          {messages.length === 0 && (
            <div className="chat-hint">
              <p className="muted">
                {applyMembrane("Send a message to start working with your agent.", membraneBypass)}
              </p>
              <div className="chat-commands-hint">
                <span className="mono muted">/skills</span>
                <span className="mono muted">/advance</span>
                <span className="mono muted">/gate</span>
                <span className="mono muted">/manifest</span>
                <span className="mono muted">/governor</span>
                <span className="mono muted">/mission</span>
                <span className="mono muted">/f3</span>
                <span className="mono muted">↑ history</span>
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`chat-msg ${msg.role}`}>
              <div className="msg-role">
                {msg.role === "user" ? "You" : msg.role === "error" ? "Error" : "Agent"}
                {msg.skill && msg.role === "agent" && (
                  <span className="msg-skill muted">
                    {" · "}{msg.skill.replace(/^skill_/, "").replace(/_v\d+$/, "")}
                  </span>
                )}
              </div>
              <div className="msg-content">{msg.content}</div>
            </div>
          ))}
          {busy && (
            <div className="chat-msg agent">
              <div className="msg-role">{applyMembrane("Agent", membraneBypass)}</div>
              <div className="msg-content typing-indicator">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            </div>
          )}
        </div>
        <div className="chat-input-bar">
          <textarea
            ref={inputRef}
            className="chat-input"
            placeholder={applyMembrane("Type a message or /command… (↑ for history)", membraneBypass)}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button className="btn-primary" onClick={send} disabled={busy || !input.trim()}>
            {busy ? "…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
